// LT-150 — Pipeline board (kanban + list view, filters, create opportunity).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Columns,
  DollarSign,
  Filter,
  List as ListIcon,
  Plus,
  Search,
  X,
} from 'lucide-react';

import PageLoader from '../../components/PageLoader';
import PageShell from '../../components/PageShell';
import { EmptyState } from '../../ui';
import { pipelinesAPI } from '../../services/pipelinesAPI';
import { opportunitiesAPI } from '../../services/opportunitiesAPI';
import { accountsAPI } from '../../services/accountsAPI';
import { COMMON_CURRENCIES, DEFAULT_CURRENCY } from '../../config/region';
import type {
  Opportunity,
  Pipeline,
  PipelineStage,
} from '../../types/pipeline';
import type { Account, Contact } from '../../types/contact';

type View = 'kanban' | 'list';

interface Filters {
  ownerId: string;
  closeDateFrom: string;
  closeDateTo: string;
  amountMin: string;
  amountMax: string;
}

const EMPTY_FILTERS: Filters = {
  ownerId: '',
  closeDateFrom: '',
  closeDateTo: '',
  amountMin: '',
  amountMax: '',
};

function fmtCurrency(amount?: number | null, currency?: string | null): string {
  if (amount === null || amount === undefined) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency ?? 'USD',
      maximumFractionDigits: 0,
    }).format(Number(amount));
  } catch {
    return `${currency ?? ''} ${amount}`;
  }
}

function fmtDate(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString();
}

function extractMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { detail?: unknown } } };
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && 'message' in (detail as Record<string, unknown>)) {
    return String((detail as Record<string, unknown>).message);
  }
  return fallback;
}

const DealsPage: React.FC = () => {
  const navigate = useNavigate();
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [activePipelineId, setActivePipelineId] = useState<string | null>(null);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>('kanban');
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [showCreate, setShowCreate] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [dragOppId, setDragOppId] = useState<string | null>(null);

  const activePipeline = useMemo(
    () => pipelines.find((p) => p.id === activePipelineId) ?? null,
    [pipelines, activePipelineId]
  );

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [pls, accts] = await Promise.all([
        pipelinesAPI.list(),
        accountsAPI.list({ limit: 500 }),
      ]);
      setPipelines(pls);
      setAccounts(accts);

      const pick =
        activePipelineId && pls.some((p) => p.id === activePipelineId)
          ? activePipelineId
          : (pls.find((p) => p.is_default) ?? pls[0])?.id ?? null;
      setActivePipelineId(pick);

      if (pick) {
        const opps = await opportunitiesAPI.list({ pipeline_id: pick, limit: 500 });
        setOpportunities(opps);
      } else {
        setOpportunities([]);
      }
    } catch (err) {
      setToast({ type: 'error', text: extractMessage(err, 'Failed to load deals') });
    } finally {
      setLoading(false);
    }
  }, [activePipelineId]);

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!activePipelineId) return;
    let cancelled = false;
    (async () => {
      try {
        const opps = await opportunitiesAPI.list({
          pipeline_id: activePipelineId,
          limit: 500,
        });
        if (!cancelled) setOpportunities(opps);
      } catch (err) {
        if (!cancelled) {
          setToast({ type: 'error', text: extractMessage(err, 'Failed to load deals') });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activePipelineId]);

  const filtered = useMemo(() => {
    const min = filters.amountMin === '' ? null : Number(filters.amountMin);
    const max = filters.amountMax === '' ? null : Number(filters.amountMax);
    const from = filters.closeDateFrom ? Date.parse(filters.closeDateFrom) : null;
    const to = filters.closeDateTo ? Date.parse(filters.closeDateTo) : null;

    return opportunities.filter((o) => {
      if (filters.ownerId && o.owner_id !== filters.ownerId) return false;
      if (min !== null && (o.amount ?? 0) < min) return false;
      if (max !== null && (o.amount ?? 0) > max) return false;
      if (o.close_date) {
        const t = Date.parse(o.close_date);
        if (from !== null && t < from) return false;
        if (to !== null && t > to) return false;
      } else if (from !== null || to !== null) {
        return false;
      }
      return true;
    });
  }, [opportunities, filters]);

  const oppsByStage = useMemo(() => {
    const m = new Map<string, Opportunity[]>();
    filtered.forEach((o) => {
      const arr = m.get(o.stage_id) ?? [];
      arr.push(o);
      m.set(o.stage_id, arr);
    });
    return m;
  }, [filtered]);

  const commitAdvance = async (
    opp: Opportunity,
    targetStage: PipelineStage
  ) => {
    const prev = opportunities;
    setOpportunities((xs) =>
      xs.map((o) =>
        o.id === opp.id ? { ...o, stage_id: targetStage.id } : o
      )
    );
    try {
      const updated = await opportunitiesAPI.advance(opp.id, {
        to_stage_id: targetStage.id,
      });
      setOpportunities((xs) => xs.map((o) => (o.id === opp.id ? updated : o)));
    } catch (err) {
      setOpportunities(prev);
      setToast({ type: 'error', text: extractMessage(err, 'Could not advance opportunity') });
    }
  };

  const handleDropOnStage = (stage: PipelineStage) => async (e: React.DragEvent) => {
    e.preventDefault();
    const oppId = e.dataTransfer.getData('text/plain') || dragOppId;
    setDragOppId(null);
    if (!oppId) return;
    const opp = opportunities.find((o) => o.id === oppId);
    if (!opp || opp.stage_id === stage.id) return;
    // §10.6: pipeline-stage drag is a pure stage move. To mark a deal
    // won/lost the operator opens the deal and uses the header
    // Mark won / Mark lost actions, which append events.
    await commitAdvance(opp, stage);
  };

  const accountName = (id: string): string =>
    accounts.find((a) => a.id === id)?.name ?? '—';

  if (loading && pipelines.length === 0) {
    return <PageLoader />;
  }

  return (
    <PageShell>
      <div className="max-w-full mx-auto px-2 sm:px-4 lg:px-8 py-4">
        <div className="mb-4 flex flex-wrap items-end gap-3 justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-gradient-to-r from-emerald-500 to-cyan-600 rounded-lg">
              <DollarSign className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-50">
              Deals
            </h1>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {pipelines.length > 1 && (
              <select
                value={activePipelineId ?? ''}
                onChange={(e) => setActivePipelineId(e.target.value)}
                className="bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-sm"
              >
                {pipelines.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.is_default ? ' (default)' : ''}
                  </option>
                ))}
              </select>
            )}
            <button
              onClick={() => setView(view === 'kanban' ? 'list' : 'kanban')}
              className="bg-white/5 border border-white/10 rounded px-2 py-1 text-gray-200 text-sm flex items-center gap-1"
              title={view === 'kanban' ? 'Switch to list view' : 'Switch to kanban'}
            >
              {view === 'kanban' ? (
                <>
                  <ListIcon className="w-4 h-4" /> List
                </>
              ) : (
                <>
                  <Columns className="w-4 h-4" /> Kanban
                </>
              )}
            </button>
            <button
              onClick={() => setShowCreate(true)}
              className="bg-emerald-600 hover:bg-emerald-700 rounded px-3 py-1 text-white text-sm flex items-center gap-1"
            >
              <Plus className="w-4 h-4" /> Create Opportunity
            </button>
          </div>
        </div>

        {toast && (
          <div
            className={`mb-3 px-3 py-2 rounded-lg text-sm flex items-center justify-between ${
              toast.type === 'success'
                ? 'bg-green-500/20 border border-green-400/40 text-green-100'
                : 'bg-red-500/20 border border-red-400/40 text-red-100'
            }`}
          >
            <span>{toast.text}</span>
            <button onClick={() => setToast(null)} className="ml-2 text-gray-400 hover:text-white">
              &times;
            </button>
          </div>
        )}

        <FiltersBar filters={filters} setFilters={setFilters} />

        {!activePipeline ? (
          <div className="mt-6 text-gray-300 text-sm">
            No pipelines in your workspace yet.{' '}
            <button
              className="text-cyan-300 underline"
              onClick={() => navigate('/settings?tab=pipelines')}
            >
              Create one in Settings
            </button>
            .
          </div>
        ) : opportunities.length === 0 ? (
          // LT-314: DS-styled empty state — true first-run case (no
          // opportunities in the pipeline at all). Filtered-empty still
          // renders in ListView/KanbanView below (intentional — the
          // filter chrome is the context the user needs).
          <EmptyState
            surface="deals_list"
            icon={DollarSign}
            headline="No deals tracked yet"
            subhead="Opportunities appear here when a sequence advances a contact into a pipeline stage, or when you add one manually from a contact's detail page."
            primary={{ label: 'Browse contacts', to: '/contacts' }}
          />
        ) : view === 'kanban' ? (
          <KanbanView
            pipeline={activePipeline}
            oppsByStage={oppsByStage}
            accountName={accountName}
            onCardClick={(id) => navigate(`/deals/${id}`)}
            onDragStart={(id) => setDragOppId(id)}
            onDropOnStage={handleDropOnStage}
          />
        ) : (
          <ListView
            opportunities={filtered}
            accounts={accounts}
            stages={activePipeline.stages}
            onRowClick={(id) => navigate(`/deals/${id}`)}
          />
        )}
      </div>

      {showCreate && activePipeline && (
        <CreateOpportunityModal
          pipeline={activePipeline}
          accounts={accounts}
          onCancel={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            void reload();
            setToast({ type: 'success', text: 'Opportunity created' });
          }}
        />
      )}
    </PageShell>
  );
};

// -------------------- sub-components --------------------

const FiltersBar: React.FC<{
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
}> = ({ filters, setFilters }) => (
  <div className="mb-3 bg-white/5 border border-white/10 rounded-lg p-2 flex flex-wrap gap-2 items-center">
    <Filter className="w-4 h-4 text-gray-400" />
    <input
      placeholder="Owner ID (optional)"
      value={filters.ownerId}
      onChange={(e) => setFilters((f) => ({ ...f, ownerId: e.target.value }))}
      className="bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-xs w-48"
    />
    <label className="text-xs text-gray-400">Close</label>
    <input
      type="date"
      value={filters.closeDateFrom}
      onChange={(e) => setFilters((f) => ({ ...f, closeDateFrom: e.target.value }))}
      className="bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-xs"
    />
    <span className="text-xs text-gray-400">→</span>
    <input
      type="date"
      value={filters.closeDateTo}
      onChange={(e) => setFilters((f) => ({ ...f, closeDateTo: e.target.value }))}
      className="bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-xs"
    />
    <label className="text-xs text-gray-400">Amount</label>
    <input
      type="number"
      placeholder="min"
      value={filters.amountMin}
      onChange={(e) => setFilters((f) => ({ ...f, amountMin: e.target.value }))}
      className="bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-xs w-20"
    />
    <span className="text-xs text-gray-400">–</span>
    <input
      type="number"
      placeholder="max"
      value={filters.amountMax}
      onChange={(e) => setFilters((f) => ({ ...f, amountMax: e.target.value }))}
      className="bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-xs w-20"
    />
    <button
      onClick={() => setFilters(EMPTY_FILTERS)}
      className="text-xs text-gray-300 hover:text-white flex items-center gap-1"
    >
      <X className="w-3.5 h-3.5" /> Clear
    </button>
  </div>
);

const KanbanView: React.FC<{
  pipeline: Pipeline;
  oppsByStage: Map<string, Opportunity[]>;
  accountName: (id: string) => string;
  onCardClick: (id: string) => void;
  onDragStart: (id: string) => void;
  onDropOnStage: (stage: PipelineStage) => (e: React.DragEvent) => void;
}> = ({ pipeline, oppsByStage, accountName, onCardClick, onDragStart, onDropOnStage }) => {
  return (
    <div className="flex gap-3 overflow-x-auto pb-3">
      {pipeline.stages.map((stage) => {
        const list = oppsByStage.get(stage.id) ?? [];
        return (
          <div
            key={stage.id}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
            }}
            onDrop={onDropOnStage(stage)}
            className={`shrink-0 w-64 bg-slate-900/40 rounded-lg p-2 border ${
              stage.is_terminal && stage.terminal_type === 'won'
                ? 'border-emerald-500/30'
                : stage.is_terminal && stage.terminal_type === 'lost'
                ? 'border-rose-500/30'
                : 'border-white/10'
            }`}
          >
            <header className="flex items-center justify-between mb-2 text-sm font-semibold text-white">
              <span>{stage.name}</span>
              <span className="text-xs text-gray-400">{list.length}</span>
            </header>
            <div className="space-y-1.5 min-h-[40px]">
              {list.map((o) => (
                <article
                  key={o.id}
                  draggable
                  onDragStart={(e) => {
                    onDragStart(o.id);
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', o.id);
                  }}
                  onClick={() => onCardClick(o.id)}
                  className="bg-slate-800/70 border border-slate-700 rounded p-2 text-xs text-white cursor-pointer hover:bg-slate-700/70"
                >
                  <div className="font-medium truncate">{o.name}</div>
                  <div className="text-gray-400 truncate">{accountName(o.account_id)}</div>
                  <div className="flex justify-between text-gray-400 mt-1">
                    <span>{fmtCurrency(o.amount, o.currency)}</span>
                    <span>{fmtDate(o.close_date)}</span>
                  </div>
                </article>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ListView: React.FC<{
  opportunities: Opportunity[];
  accounts: Account[];
  stages: PipelineStage[];
  onRowClick: (id: string) => void;
}> = ({ opportunities, accounts, stages, onRowClick }) => {
  const stageMap = useMemo(() => {
    const m = new Map<string, PipelineStage>();
    stages.forEach((s) => m.set(s.id, s));
    return m;
  }, [stages]);
  const accountMap = useMemo(() => {
    const m = new Map<string, Account>();
    accounts.forEach((a) => m.set(a.id, a));
    return m;
  }, [accounts]);

  return (
    <div className="bg-white/5 border border-white/10 rounded-lg overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-gray-400 text-xs uppercase tracking-wide">
            <th className="px-3 py-2 text-left">Name</th>
            <th className="px-3 py-2 text-left">Account</th>
            <th className="px-3 py-2 text-left">Stage</th>
            <th className="px-3 py-2 text-right">Amount</th>
            <th className="px-3 py-2 text-left">Close date</th>
            <th className="px-3 py-2 text-left">Created</th>
          </tr>
        </thead>
        <tbody>
          {opportunities.map((o) => (
            <tr
              key={o.id}
              onClick={() => onRowClick(o.id)}
              className="border-t border-white/5 text-white hover:bg-white/5 cursor-pointer"
            >
              <td className="px-3 py-2">{o.name}</td>
              <td className="px-3 py-2 text-gray-300">
                {accountMap.get(o.account_id)?.name ?? '—'}
              </td>
              <td className="px-3 py-2 text-gray-300">
                {stageMap.get(o.stage_id)?.name ?? '—'}
              </td>
              <td className="px-3 py-2 text-right">{fmtCurrency(o.amount, o.currency)}</td>
              <td className="px-3 py-2 text-gray-300">{fmtDate(o.close_date)}</td>
              <td className="px-3 py-2 text-gray-400">{fmtDate(o.created_at)}</td>
            </tr>
          ))}
          {opportunities.length === 0 && (
            <tr>
              <td colSpan={6} className="px-3 py-6 text-center text-gray-400">
                No opportunities match the current filters.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export const CreateOpportunityModal: React.FC<{
  pipeline: Pipeline;
  accounts: Account[];
  onCancel: () => void;
  onCreated: () => void;
}> = ({ pipeline, accounts, onCancel, onCreated }) => {
  const [name, setName] = useState('');
  const [accountId, setAccountId] = useState<string>('');
  const [contactId, setContactId] = useState<string>('');
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactSearch, setContactSearch] = useState('');
  const [stageId, setStageId] = useState<string>(pipeline.stages[0]?.id ?? '');
  const [amount, setAmount] = useState('');
  // LT-337: prefill currency from deployment region (AUD for AU, INR for IN).
  // User can override via the dropdown for cross-border deals.
  const [currency, setCurrency] = useState<string>(DEFAULT_CURRENCY);
  const [closeDate, setCloseDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!accountId) {
        setContacts([]);
        return;
      }
      try {
        const list = await accountsAPI.listContacts(accountId, { limit: 200 });
        if (!cancelled) {
          setContacts(list);
          if (list.length > 0 && !contactId) setContactId(list[0].id);
        }
      } catch {
        if (!cancelled) setContacts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const submit = async () => {
    if (!name.trim() || !accountId || !contactId) {
      setErr('Name, account, and primary contact are required.');
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      await opportunitiesAPI.create({
        name: name.trim(),
        account_id: accountId,
        primary_contact_id: contactId,
        pipeline_id: pipeline.id,
        stage_id: stageId || undefined,
        amount: amount === '' ? null : Number(amount),
        currency: currency.trim() || null,
        close_date: closeDate || null,
      });
      onCreated();
    } catch (e) {
      setErr(extractMessage(e, 'Could not create opportunity'));
    } finally {
      setSubmitting(false);
    }
  };

  const filteredAccounts = contactSearch.trim()
    ? accounts.filter((a) =>
        a.name.toLowerCase().includes(contactSearch.trim().toLowerCase())
      )
    : accounts;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-lg max-w-lg w-full p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-white text-lg font-semibold">Create Opportunity</h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-white">
            &times;
          </button>
        </div>

        {err && (
          <div className="bg-red-500/20 border border-red-400/40 text-red-100 rounded px-3 py-2 text-sm">
            {err}
          </div>
        )}

        <div>
          <label className="block text-xs text-gray-400 mb-1">Name *</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded p-2 text-white text-sm"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-400 mb-1">Account *</label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 w-3.5 h-3.5 text-gray-400" />
              <input
                placeholder="Filter accounts…"
                value={contactSearch}
                onChange={(e) => setContactSearch(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded pl-7 p-2 text-white text-sm"
              />
            </div>
          </div>
          <select
            value={accountId}
            onChange={(e) => {
              setAccountId(e.target.value);
              setContactId('');
            }}
            className="mt-1 w-full bg-white/5 border border-white/10 rounded p-2 text-white text-sm"
          >
            <option value="">Select account…</option>
            {filteredAccounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-400 mb-1">Primary contact *</label>
          <select
            value={contactId}
            onChange={(e) => setContactId(e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded p-2 text-white text-sm"
          >
            <option value="">Select contact…</option>
            {contacts.map((c) => {
              const fn = c.first_name ?? '';
              const ln = c.last_name ?? '';
              const full = `${fn} ${ln}`.trim() || c.email || c.phone || c.id;
              return (
                <option key={c.id} value={c.id}>
                  {full}
                </option>
              );
            })}
          </select>
        </div>

        <div className="grid grid-cols-4 gap-2">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Stage</label>
            <select
              value={stageId}
              onChange={(e) => setStageId(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded p-2 text-white text-sm"
            >
              {pipeline.stages.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Amount</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded p-2 text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Currency</label>
            <select
              aria-label="Currency"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded p-2 text-white text-sm"
            >
              {COMMON_CURRENCIES.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Close date</label>
            <input
              type="date"
              value={closeDate}
              onChange={(e) => setCloseDate(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded p-2 text-white text-sm"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onCancel}
            className="px-3 py-1 text-sm text-gray-200 hover:text-white"
          >
            Cancel
          </button>
          <button
            onClick={() => void submit()}
            disabled={submitting}
            className="px-3 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-sm disabled:opacity-50"
          >
            {submitting ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DealsPage;
